import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import API_URL from '../config/API_URL';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your payment...');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard/settings');
      return;
    }

    verifyPayment();
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      // Call our backend to process the checkout session
      const response = await fetch(`${API_URL}/api/subscription/process-checkout/${sessionId}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
        setMessage('Premium subscription activated!');
        
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/dashboard/settings');
        }, 2000);
      } else {
        setStatus('error');
        setMessage('Payment processing failed. Please contact support.');
        setTimeout(() => {
          navigate('/dashboard/settings');
        }, 3000);
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setStatus('error');
      setMessage('Payment processing failed. Please contact support.');
      setTimeout(() => {
        navigate('/dashboard/settings');
      }, 3000);
    }
  };
  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem',
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      padding: '3rem',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center',
      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
    },
    icon: {
      marginBottom: '1.5rem',
      display: 'flex',
      justifyContent: 'center',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '1rem',
    },
    message: {
      fontSize: '1.1rem',
      color: '#64748b',
      marginBottom: '2rem',
    },
    loader: {
      display: 'inline-block',
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>
            {status === 'processing' && (
              <div style={styles.loader}>
                <Loader size={64} color="#667eea" />
              </div>
            )}
            {status === 'success' && (
              <CheckCircle size={64} color="#10b981" />
            )}
            {status === 'error' && (
              <CheckCircle size={64} color="#ef4444" />
            )}
          </div>
          
          <h1 style={styles.title}>
            {status === 'processing' && 'Processing Payment'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Almost There'}
          </h1>
          
          <p style={styles.message}>{message}</p>
          
          {status === 'processing' && (
            <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
              Please wait while we confirm your payment...
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentSuccess;